/**
 * ④ The exit questionnaire — read here, written in the survey builder.
 *
 * **This block does not save anything, and that is the design.** The link between a feature and its
 * exit questionnaire is a column on the *survey* (`Survey.requiresOptInFeatureId`); there is no
 * field on the feature pointing back, by design (R23). A selector here would mean that pressing
 * "Salvar alterações" on a feature also issued a `PATCH /v1/surveys/<id>` — a write to another
 * aggregate, in another module, behind another permission (`survey:update`). Three things follow,
 * and each of them is worse than a link:
 *
 * - somebody who may edit features but not surveys would be shown a control that answers `403`
 *   from an endpoint they never asked for;
 * - "Nenhum" would have no payload to send: detaching by `requiresOptInFeatureId: null` is a `400`,
 *   so the only implementation is silently repointing or re-triggering the survey;
 * - one button doing two writes fails in halves — the feature saved, the survey `PATCH` refused,
 *   and the screen reporting success over a link that was never written.
 *
 * So the block reads the surveys that name this feature and offers the way to where the decision
 * lives. What it *does* own is saying when the answer is "ninguém está sendo perguntado":
 *
 * **R35 is the whole reason this block exists.** A questionnaire in draft, switched off, outside its
 * window or attached to nothing is simply not served: the bootstrap answers `exitSurveyId: null`,
 * the modal confirms without asking, and **no error is raised anywhere**. Silence is the worst way
 * for this to fail, and the screen is the only defence.
 *
 * **R45 — one per feature, so there is no "criar outro".** The button used to be offered whatever
 * was already attached, and the second questionnaire was accepted: the bootstrap then served the
 * newest and the other one sat approved and active collecting nothing. The BFF now refuses it with
 * `409` and a partial unique index makes it impossible, so the button is gone rather than disabled
 * — a disabled button says "not now" and sends the person looking for the condition that unlocks
 * it, when there is none. A sentence takes its place, naming the two real ways out: edit the one
 * that is there, or delete it, which frees the slot.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton, UTLoading } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExitSurveys } from '../../../Hooks/useExitSurveys';
import { usePrivileges } from '@/Hooks/usePrivileges/usePrivilleges';
import type { Survey } from '@/Pages/BUs/Surveys/Types/Surveys.types';
import {
  EXIT_STATE_CONSEQUENCES,
  EXIT_STATE_LABELS,
  EXIT_STATE_TONES,
  exitSurveyState,
  surveyTitle,
} from '@/Pages/BUs/Surveys/Utils/surveyFormat';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding-top: var(--space-m);
  border-top: 1px solid var(--color-divider);
`;

const headStyle = css`
  display: flex;
  align-items: baseline;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const indexStyle = css`
  font-size: var(--font-size-md);
  color: var(--color-primary);
`;

const titleStyle = css`
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
`;

const hintStyle = css`
  margin: 0;
  flex: 1 1 260px;
  min-width: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const spacerStyle = css`
  flex: 1;
`;

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
  padding: var(--space-s) var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
`;

const rowIdentityStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const rowTitleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const rowMetaStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 4px var(--space-s);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const slugStyle = css`
  font-family: var(--font-mono);
`;

const consequenceStyle = css`
  flex: 1 1 100%;
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--gl-yellow-dark);
  text-wrap: pretty;
`;

/** The one state that is not a warning: this is the questionnaire people actually get. */
const servedStyle = css`
  flex: 1 1 100%;
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const emptyStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-s);
  padding: var(--space-m) var(--space-ml);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const warningStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-s);
  padding: var(--space-m) var(--space-ml);
  border: 1px solid var(--color-warning);
  border-radius: var(--radius-m);
  background: var(--color-warning-subtle);
  font-size: var(--font-size-xs);
  text-wrap: pretty;
`;

const listStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
`;

/** Replaces the "criar outro" button: the rule, and the two ways out of it. */
const singleNoteStyle = css`
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

export interface OptInExitSurveyBlockProps {
  /** Absent while the feature is being created — there is nothing for a survey to point at yet. */
  featureId?: string;
  /** The feature's `key`, which seeds the new survey's slug as `<key>-saida`. */
  featureKey: string;
  tenantSlug: string;
  /** `false` puts everyone back on the old behaviour, so no questionnaire is served either. */
  isFeatureActive: boolean;
}

export const OptInExitSurveyBlock = ({
  featureId,
  featureKey,
  tenantSlug,
  isFeatureActive,
}: OptInExitSurveyBlockProps): JSX.Element => {
  const navigate = useNavigate();
  const { pages: privileges } = usePrivileges();

  const { surveys, served, isLoading } = useExitSurveys(featureId);

  const goToBuilder = (survey?: Survey): void => {
    navigate(
      survey
        ? `/surveys/${survey.id}`
        : `/surveys/new?optInFeatureId=${featureId ?? ''}&tenantSlug=${tenantSlug}&optInFeatureKey=${encodeURIComponent(featureKey)}`,
    );
  };

  return (
    <section id='opt-in-feature-exit-survey' className={wrapStyle}>
      <div className={headStyle}>
        <span className={indexStyle}>④</span>
        <span className={titleStyle}>Questionário de saída</span>
        <p className={hintStyle}>
          O que é perguntado a quem desativa esta funcionalidade. É uma pesquisa, com perguntas,
          versões e traduções próprias — e o vínculo é escrito nela, no módulo Pesquisas, não aqui.
        </p>
      </div>

      {!featureId ? (
        <div className={emptyStyle}>
          <GLIcon variant='Communication.Info' size={16} fill='var(--color-text-muted)' />
          <span>
            Salve a funcionalidade primeiro. O questionário de saída aponta para ela pela chave, e
            até ela existir não há para onde apontar.
          </span>
        </div>
      ) : null}

      {featureId && isLoading ? <UTLoading type='dots' /> : null}

      {featureId && !isLoading && surveys.length === 0 ? (
        <div className={emptyStyle}>
          <GLIcon variant='Communication.Info' size={16} fill='var(--color-text-muted)' />
          <span>
            Nenhum questionário de saída. O modal de desativar mostra só a confirmação —{' '}
            <strong>o que é uma escolha válida</strong>, não um cadastro pela metade.
          </span>
          <div className={spacerStyle} />
          {privileges.BUSINESS_UNITS.SURVEYS.CREATE ? (
            <UTButton
              fitContent
              variant='secondary'
              size='small'
              id='opt-in-feature-exit-survey-create'
              text='Criar questionário de saída'
              icon='Arrows.Plus'
              onClick={() => {
                goToBuilder();
              }}
            />
          ) : null}
        </div>
      ) : null}

      {featureId && !isLoading && surveys.length > 0 ? (
        <>
          {/*
            R35, said once and at the top: with questionnaires attached but none of them servable,
            the modal confirms in silence and nothing anywhere reports a problem.
          */}
          {!served ? (
            <div id='opt-in-feature-exit-survey-warning' className={warningStyle} role='alert'>
              <GLIcon
                variant='Notifications.WarningCircle'
                size={16}
                fill='var(--gl-yellow-dark)'
              />
              <span>
                <strong>Ninguém está sendo perguntado.</strong> Há questionário de saída cadastrado,
                mas nenhum deles é entregue no estado em que está — e isso não gera erro em lugar
                nenhum: o modal simplesmente confirma, sem perguntar nada.
              </span>
            </div>
          ) : null}

          {served && !isFeatureActive ? (
            <div id='opt-in-feature-exit-survey-inactive' className={warningStyle} role='alert'>
              <GLIcon
                variant='Notifications.WarningCircle'
                size={16}
                fill='var(--gl-yellow-dark)'
              />
              <span>
                A funcionalidade está <strong>desativada</strong>, então ela não chega a ninguém e
                este questionário também não — não há saída para perguntar.
              </span>
            </div>
          ) : null}

          <div className={listStyle}>
            {surveys.map(survey => {
              const state = exitSurveyState(survey);
              const isServed = served?.id === survey.id;

              return (
                <div
                  key={survey.id}
                  id={`opt-in-feature-exit-survey-${survey.id}`}
                  className={rowStyle}
                >
                  <div className={rowIdentityStyle}>
                    <span className={rowTitleStyle}>{surveyTitle(survey)}</span>
                    <span className={rowMetaStyle}>
                      <span className={slugStyle}>{survey.slug}</span>
                      <span>·</span>
                      <span>v{survey.version}</span>
                      {survey.draftVersion !== null ? (
                        <>
                          <span>·</span>
                          <span>v{survey.draftVersion} em rascunho</span>
                        </>
                      ) : null}
                    </span>
                  </div>

                  <div className={spacerStyle} />

                  <StatusTag
                    id={`opt-in-feature-exit-survey-state-${survey.id}`}
                    status={EXIT_STATE_TONES[state]}
                    size='sm'
                    dot
                  >
                    {EXIT_STATE_LABELS[state]}
                  </StatusTag>

                  {privileges.BUSINESS_UNITS.SURVEYS.EDIT ? (
                    <UTButton
                      fitContent
                      variant='secondary'
                      size='small'
                      id={`opt-in-feature-exit-survey-edit-${survey.id}`}
                      text='Editar'
                      icon='Settings.PencilSimple'
                      onClick={() => {
                        goToBuilder(survey);
                      }}
                    />
                  ) : null}

                  {/*
                    A feature has one exit questionnaire (R45), so the usual sentence is the short
                    one. The "mais recente" branch survives for rows written before the BFF started
                    refusing the second — with two servable, it keeps the newest, and saying so is
                    cheaper than someone editing the wrong one and waiting for answers.
                  */}
                  {isServed ? (
                    <p className={servedStyle}>
                      {surveys.length > 1
                        ? 'É esta que aparece no modal: entre as entregáveis, vale a mais recente.'
                        : 'É esta que aparece no modal de saída.'}
                    </p>
                  ) : (
                    <p className={consequenceStyle}>{EXIT_STATE_CONSEQUENCES[state]}</p>
                  )}
                </div>
              );
            })}
          </div>

          {/*
            No "criar outro" button here, and no disabled one either (R45). A feature has at most
            one live exit questionnaire: the BFF refuses the second with `409` and a partial unique
            index makes it impossible, so a button offering it would be a button that always fails.
            A disabled button would be worse — it says "not now" and leaves the person hunting for
            the condition that unlocks it, when there is none. What replaces it is the sentence
            saying why, and where the two real ways out are.
          */}
          <p id='opt-in-feature-exit-survey-single' className={singleNoteStyle}>
            Uma funcionalidade tem <strong>um</strong> questionário de saída. Para mudar o que é
            perguntado, edite o que está aí; para começar outro do zero, exclua este primeiro —
            excluir libera o lugar.
          </p>
        </>
      ) : null}
    </section>
  );
};
